/** DryRunReport — plan retourné par POST /dry-run/{upload_id}. */
import React, { useState } from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Alert, AlertDescription } from "../ui/alert";
import { AlertTriangle, ArrowRight, Database, Package as PackageIcon, RefreshCw } from "lucide-react";
import TypedConfirmationField from "./TypedConfirmationField";

export default function DryRunReport({ plan, uploadId, onInstall, busy }) {
  const [typedOk, setTypedOk] = useState(false);
  const [typedValue, setTypedValue] = useState("");
  if (!plan) return null;

  const compatOk = (plan.min_current_version_ok !== false) && (plan.min_schema_ok !== false);
  const nonRev = Boolean(plan.has_non_reversible);
  const canInstall = compatOk && (!nonRev || typedOk);

  return (
    <Card className="p-6 space-y-4" data-testid="dryrun-report">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-lg font-medium">{plan.current_version || "?"}</span>
        <ArrowRight className="h-5 w-5 text-muted-foreground" />
        <span className="text-lg font-semibold text-primary" data-testid="target-version">{plan.target_version}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Flag label="Rebuild frontend" value={plan.frontend_build_included} />
        <Flag label="Restart backend requis" value={plan.backend_restart_required} />
        <Flag label="Dépendances changées" value={plan.dependencies_changed} />
      </div>

      <div className="border-t pt-3">
        <p className="text-sm font-medium mb-2 flex items-center gap-2"><Database className="h-4 w-4" /> Migrations pendantes</p>
        {plan.migrations_pending && plan.migrations_pending.length > 0 ? (
          <ul className="text-sm space-y-1">
            {plan.migrations_pending.map((m) => (
              <li key={m} className="flex items-center gap-2">
                <Badge variant="outline">{m}</Badge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">Aucune migration à appliquer.</p>
        )}
      </div>

      {plan.warnings?.length > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc pl-4">{plan.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </AlertDescription>
        </Alert>
      )}

      {nonRev && (
        <Alert variant="destructive" data-testid="non-reversible-warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Migration(s) non réversible(s) détectée(s).</AlertDescription>
        </Alert>
      )}
      {plan.min_current_version_ok === false && (
        <Alert variant="destructive"><AlertDescription>Version actuelle incompatible.</AlertDescription></Alert>
      )}
      {plan.min_schema_ok === false && (
        <Alert variant="destructive"><AlertDescription>Schéma de base incompatible.</AlertDescription></Alert>
      )}

      {nonRev && (
        <TypedConfirmationField onConfirm={(ok, v) => { setTypedOk(ok); setTypedValue(v); }} />
      )}

      <div className="flex justify-end pt-2">
        <Button
          disabled={!canInstall || busy}
          onClick={() => onInstall?.(uploadId, nonRev ? typedValue : null)}
          data-testid="btn-install"
        >
          {busy ? (<><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Installation…</>) : "Installer cette version"}
        </Button>
      </div>
    </Card>
  );
}

function Flag({ label, value }) {
  return (
    <div className="flex items-center justify-between border border-border rounded-md p-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Badge className={value ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-700"}>{value ? "oui" : "non"}</Badge>
    </div>
  );
}
